import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { SWIPE_MIN_PX } from '@constants/layout';
import { LIGHTBOX } from '@locales/id/messages';
import { useBodyClass } from '@lib/hooks';
import { IconButton } from './IconButton';
import { Icon } from './Icon';
import { useModalFocus } from './useModalFocus';

export interface LightboxItem {
  src: string;
  /** a smaller copy already in the cache, shown until `src` has loaded */
  placeholder?: string;
  alt: string;
}

/**
 * Full-screen viewer for a set of pictures: ← / → (keys, buttons, a swipe on touch screens), "3 / 7", Escape or
 * the close button. Focus stays inside while it is open and goes back where it came from. The neighbours are
 * fetched ahead so turning the page is instant.
 */
export function Lightbox({ items, index, onIndex, onClose }: { items: LightboxItem[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const at = Math.min(Math.max(0, index), items.length - 1);
  const item = items[at];
  const [shown, setShown] = useState<{ src: string; loaded: boolean; failed: boolean }>({ src: item.src, loaded: false, failed: false });
  const s = shown.src === item.src ? shown : { src: item.src, loaded: false, failed: false };
  // one finger only: a second one (pinch) cancels the swipe
  const start = useRef<{ id: number; x: number; y: number } | null>(null);
  const fingers = useRef(0);
  useBodyClass('no-scroll');
  useModalFocus(ref, onClose);
  const go = (d: number) => {
    const n = at + d;
    if (n >= 0 && n < items.length) onIndex(n);
  };
  const goRef = useRef(go);
  goRef.current = go;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowLeft') goRef.current(-1);
      if (e.key === 'ArrowRight') goRef.current(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    [items[at - 1], items[at + 1]].forEach((n) => {
      if (n) new Image().src = n.src;
    });
  }, [items, at]);

  const down = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse') return;
    fingers.current++;
    start.current = fingers.current === 1 ? { id: e.pointerId, x: e.clientX, y: e.clientY } : null;
  };
  const up = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse') return;
    fingers.current = Math.max(0, fingers.current - 1);
    const p = start.current;
    start.current = null;
    // a zoomed-in picture is being panned, not turned
    if (!p || p.id !== e.pointerId || (window.visualViewport?.scale ?? 1) > 1) return;
    const dx = e.clientX - p.x;
    if (Math.abs(dx) > SWIPE_MIN_PX && Math.abs(dx) > Math.abs(e.clientY - p.y)) go(dx < 0 ? 1 : -1);
  };
  const cancel = () => {
    fingers.current = 0;
    start.current = null;
  };

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={LIGHTBOX.label} ref={ref}>
      <div className="lightbox-bar">
        <span className="lightbox-count" aria-live="polite">{LIGHTBOX.counter(at + 1, items.length)}</span>
        <IconButton icon="close" label={LIGHTBOX.close} onClick={onClose} className="lightbox-btn" data-autofocus />
      </div>
      <div className="lightbox-stage" onPointerDown={down} onPointerUp={up} onPointerCancel={cancel} onClick={(e) => e.target === e.currentTarget && onClose()}>
        {s.failed ? (
          <p className="lightbox-failed"><Icon name="image" size={28} />{LIGHTBOX.failed}</p>
        ) : (
          <>
            {!s.loaded && item.placeholder && <img className="lightbox-img placeholder" src={item.placeholder} alt="" aria-hidden="true" draggable={false} />}
            {!s.loaded && <span className="spinner lightbox-spinner" aria-hidden="true" />}
            <img
              key={item.src}
              className={`lightbox-img ${s.loaded ? 'loaded' : ''}`}
              src={item.src}
              alt={item.alt}
              draggable={false}
              onLoad={() => setShown({ src: item.src, loaded: true, failed: false })}
              onError={() => setShown({ src: item.src, loaded: false, failed: true })}
            />
          </>
        )}
      </div>
      {items.length > 1 && (
        <>
          {/* aria-disabled, not disabled: the button keeps focus at either end instead of dropping it to the page */}
          <IconButton icon="back" label={LIGHTBOX.prev} onClick={() => go(-1)} aria-disabled={at === 0} className="lightbox-btn lightbox-prev" />
          <IconButton icon="next" label={LIGHTBOX.next} onClick={() => go(1)} aria-disabled={at === items.length - 1} className="lightbox-btn lightbox-next" />
        </>
      )}
    </div>
  );
}
